import React from 'react';
import clsx from 'clsx';
import FeatureIntro from '../../components/common/FeatureIntro';
import styles from './ReportsView.module.css';
import type { ReportDefinition, ReportId } from './reportDefinitions';
import { getReportDefinition, reportDefinitions } from './reportDefinitions';

const REPORTS_MANUAL_URL =
  import.meta.env.VITE_REPORTS_MANUAL_URL ??
  '/reports-library.html';

interface ReportsViewProps {
  className?: string;
}

const REPORTS_ROUTE_HASH = '#reports';

const parseReportIdFromHash = (): ReportId | null => {
  const hash = window.location.hash || '';
  if (!hash.startsWith(REPORTS_ROUTE_HASH)) return null;
  const [, query = ''] = hash.split('?');
  if (!query) return null;
  const params = new URLSearchParams(query);
  const reportId = params.get('report');
  const isValid = reportDefinitions.some((report) => report.id === reportId);
  return isValid ? (reportId as ReportId) : null;
};

const updateHashWithReport = (reportId: ReportId | null): void => {
  const nextHash = reportId ? `${REPORTS_ROUTE_HASH}?report=${reportId}` : REPORTS_ROUTE_HASH;
  if (window.location.hash === nextHash) return;
  window.location.hash = nextHash;
};

const ReportsView: React.FC<ReportsViewProps> = ({ className = '' }) => {
  const [selectedReportId, setSelectedReportId] = React.useState<ReportId | null>(() => {
    if (typeof window === 'undefined') return null;
    return parseReportIdFromHash();
  });

  const selectedReport: ReportDefinition | undefined = React.useMemo(() => {
    return getReportDefinition(selectedReportId);
  }, [selectedReportId]);

  React.useEffect(() => {
    const handleHashChange = () => {
      setSelectedReportId(parseReportIdFromHash());
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleOpenReport = (reportId: ReportId) => {
    setSelectedReportId(reportId);
    updateHashWithReport(reportId);
  };

  const handleExitReport = () => {
    setSelectedReportId(null);
    updateHashWithReport(null);
  };

  if (selectedReport) {
    const ReportComponent = selectedReport.component;
    const showCleaningNotice = ['descending-order', 'seed-source-programs'].includes(selectedReport.id);

    return (
      <div className={clsx(styles.container, styles.viewer, className)} data-testid="reports-viewer">
        <div className={styles.panel}>
          <div className={styles.viewerHeader}>
            <div className={styles.viewerHeading}>
              <h1 className={styles.viewerTitle}>{selectedReport.name}</h1>
              {selectedReport.headline ? (
                <p className={styles.viewerSubtitle}>{selectedReport.headline}</p>
              ) : null}
              {showCleaningNotice ? (
                <p className={styles.viewerAlert}>Data cleaning in progress</p>
              ) : null}
            </div>
            <button type="button" className={styles.backButton} onClick={handleExitReport}>
              ← Back to reports
            </button>
          </div>
          <div className={styles.viewerBody}>
            <ReportComponent onBack={handleExitReport} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx(styles.container, className)} data-testid="reports-catalog">
      <FeatureIntro
        eyebrow="Reports Library"
        description="Live reporting on BAM data. Run your standard reports on structured tables from the BAMdb."
        note={(
          <span>
            Need guidance?{' '}
            <a href={REPORTS_MANUAL_URL} target="_blank" rel="noopener noreferrer">
              Open the Reports Library Guide
            </a>{' '}
            for refresh, print, and vetting tips.
          </span>
        )}
      />

      <section className={styles.catalogPanel} aria-label="Available reports">
        <div className={styles.cardsGrid}>
          {reportDefinitions.map((report) => (
            <article key={report.id} className={styles.card} data-testid={`report-card-${report.id}`}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>{report.name}</h2>
                <p className={styles.cardHeadline}>{report.headline}</p>
                <p className={styles.cardDescription}>{report.description}</p>
              </div>
              <div className={styles.cardActions}>
                <button type="button" className={styles.actionButton} onClick={() => handleOpenReport(report.id)}>
                  View report
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default ReportsView;
